import { useState } from 'react'
import { jsPDF } from 'jspdf'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { manualChapters, MANUAL_VERSION } from '@/data/manual'
import { useTheme } from '@/contexts/ThemeContext'

interface ManualPdfGeneratorProps {
  className?: string
}

// Data in italiano esteso - ANNO 2025!
const MANUAL_DATE_ITALIAN = '14 Dicembre 2025'

// Funzione per pulire il testo da caratteri non supportati
function cleanText(text: string, appName: string = 'GrowUp!'): string {
  // Rimuovi emoji e caratteri Unicode speciali
  let result = text
    // Sostituisci placeholder APP_NAME
    .replace(/\{\{APP_NAME\}\}/g, appName)
    // Emoji comuni
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .replace(/[\u{2700}-\u{27BF}]/gu, '')
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
    // Simboli vari
    .replace(/[✓✅❌⚠️💡🔗📝🔒🔓👤📧🕐📌🎮🏅🥇🥈🥉➡️⬅️↑↓🔍💻📱🖥️⏱️🎬🎧📸✏️🗑️➕➖]/g, '')
    .replace(/[@]/g, '@')

  return result
}

export default function ManualPdfGenerator({ className }: ManualPdfGeneratorProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const { theme } = useTheme()

  const generatePdf = async () => {
    setIsGenerating(true)
    setProgress(0)

    // Local helper che usa automaticamente theme.appName
    const clean = (text: string) => cleanText(text, theme.appName)

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })

      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 20
      const contentWidth = pageWidth - margin * 2
      let yPos = margin

      // Helper: nuova pagina
      const newPage = () => {
        doc.addPage()
        yPos = margin + 20
      }

      // Helper: controlla se serve nuova pagina
      const checkPage = (needed: number = 20) => {
        if (yPos + needed > pageHeight - 15) {
          newPage()
          return true
        }
        return false
      }

      // Helper: aggiungi testo con wrap
      const addText = (text: string, size: number, bold: boolean = false, color: [number, number, number] = [0, 0, 0]) => {
        const cleaned = clean(text)
        if (!cleaned.trim()) return

        doc.setFontSize(size)
        doc.setFont('helvetica', bold ? 'bold' : 'normal')
        doc.setTextColor(color[0], color[1], color[2])

        const lines = doc.splitTextToSize(cleaned, contentWidth)
        const lineH = size * 0.45

        for (const line of lines) {
          checkPage(lineH + 2)
          doc.text(line, margin, yPos)
          yPos += lineH
        }
      }

      // ========== COPERTINA ==========
      setProgress(5)

      // Sfondo
      doc.setFillColor(71, 85, 105)
      doc.rect(0, 0, pageWidth, pageHeight, 'F')

      // Box bianco
      doc.setFillColor(255, 255, 255)
      doc.roundedRect(margin, 50, contentWidth, 90, 5, 5, 'F')

      // Nome azienda
      doc.setTextColor(71, 85, 105)
      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.text(theme.companyName, pageWidth / 2, 70, { align: 'center' })

      // Nome app
      doc.setTextColor(249, 115, 22)
      doc.setFontSize(36)
      doc.setFont('helvetica', 'bold')
      doc.text(theme.appName, pageWidth / 2, 95, { align: 'center' })

      // Tagline
      doc.setTextColor(100, 116, 139)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'normal')
      doc.text(theme.tagline, pageWidth / 2, 115, { align: 'center' })

      // Titolo manuale
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(28)
      doc.setFont('helvetica', 'bold')
      doc.text('MANUALE UTENTE', pageWidth / 2, 175, { align: 'center' })

      // Versione e data
      doc.setFontSize(12)
      doc.setFont('helvetica', 'normal')
      doc.text(`Versione ${MANUAL_VERSION}`, pageWidth / 2, 195, { align: 'center' })
      doc.text(`Ultimo aggiornamento: ${MANUAL_DATE_ITALIAN}`, pageWidth / 2, 207, { align: 'center' })

      // Copyright
      doc.setFontSize(10)
      doc.text(theme.copyright, pageWidth / 2, pageHeight - 15, { align: 'center' })

      // ========== INDICE ==========
      setProgress(10)
      newPage()

      doc.setTextColor(71, 85, 105)
      doc.setFontSize(28)
      doc.setFont('helvetica', 'bold')
      doc.text('Indice', margin, yPos)
      yPos += 15

      // Numeri pagina stimati per indice
      let pageNum = 4 // dopo copertina + indice (2 pagine circa)
      const tocData: Array<{title: string, page: number, isChapter: boolean}> = []

      for (const chapter of manualChapters) {
        tocData.push({ title: `${chapter.number}. ${chapter.title}`, page: pageNum, isChapter: true })
        for (const section of chapter.sections) {
          tocData.push({ title: section.title, page: pageNum, isChapter: false })
          pageNum += Math.ceil(section.content.length / 1500) // stima
        }
        pageNum++
      }

      // Stampa indice
      for (const item of tocData) {
        checkPage(8)

        const indent = item.isChapter ? 0 : 8
        doc.setFontSize(item.isChapter ? 11 : 10)
        doc.setFont('helvetica', item.isChapter ? 'bold' : 'normal')
        doc.setTextColor(item.isChapter ? 31 : 75, item.isChapter ? 41 : 85, item.isChapter ? 55 : 99)

        // Titolo
        const title = clean(item.title)
        doc.text(title, margin + indent, yPos)

        // Numero pagina
        doc.text(item.page.toString(), pageWidth - margin, yPos, { align: 'right' })

        yPos += item.isChapter ? 7 : 5
      }

      // ========== CAPITOLI ==========
      const totalSections = manualChapters.reduce((acc, ch) => acc + ch.sections.length, 0)
      let processed = 0

      for (const chapter of manualChapters) {
        // Pagina titolo capitolo
        newPage()

        // Box capitolo
        doc.setFillColor(249, 115, 22)
        doc.roundedRect(margin, yPos - 5, 55, 18, 3, 3, 'F')
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text(`Capitolo ${chapter.number}`, margin + 5, yPos + 6)
        yPos += 20

        // Titolo capitolo
        doc.setTextColor(31, 41, 55)
        doc.setFontSize(26)
        doc.setFont('helvetica', 'bold')
        doc.text(clean(chapter.title), margin, yPos)
        yPos += 12

        // Descrizione
        doc.setTextColor(100, 116, 139)
        doc.setFontSize(12)
        doc.setFont('helvetica', 'normal')
        doc.text(clean(chapter.description), margin, yPos)
        yPos += 15

        // Sezioni
        for (const section of chapter.sections) {
          processed++
          setProgress(10 + Math.floor((processed / totalSections) * 85))

          checkPage(30)

          // Titolo sezione
          doc.setTextColor(249, 115, 22)
          doc.setFontSize(14)
          doc.setFont('helvetica', 'bold')
          doc.text(clean(section.title), margin, yPos)
          yPos += 8

          // Contenuto - parsing semplificato del markdown
          const lines = section.content.split('\n')

          for (const line of lines) {
            const trimmed = line.trim()

            // Salta righe vuote
            if (!trimmed) {
              yPos += 2
              continue
            }

            // Salta immagini e didascalie
            if (trimmed.startsWith('![') || (trimmed.startsWith('*') && trimmed.endsWith('*') && !trimmed.startsWith('**'))) {
              continue
            }

            // Headers ##
            if (trimmed.startsWith('## ')) {
              continue // gia' gestito come titolo sezione
            }

            // Headers ###
            if (trimmed.startsWith('### ')) {
              checkPage(12)
              yPos += 3
              addText(trimmed.replace('### ', ''), 12, true, [31, 41, 55])
              yPos += 2
              continue
            }

            // Headers ####
            if (trimmed.startsWith('#### ')) {
              checkPage(10)
              yPos += 2
              addText(trimmed.replace('#### ', ''), 11, true, [55, 65, 81])
              continue
            }

            // Tabelle
            if (trimmed.startsWith('|')) {
              // Salta separatori
              if (trimmed.match(/^\|[\s\-:|]+\|$/)) continue

              checkPage(6)
              const tableRow = clean(trimmed.replace(/\|/g, '  ').trim())
              if (tableRow) {
                doc.setFontSize(9)
                doc.setFont('helvetica', 'normal')
                doc.setTextColor(55, 65, 81)
                doc.text(tableRow, margin, yPos)
                yPos += 4
              }
              continue
            }

            // Liste puntate
            if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
              checkPage(6)
              const bullet = clean(trimmed.replace(/^[-*]\s+/, '').replace(/\*\*/g, ''))
              doc.setFontSize(10)
              doc.setFont('helvetica', 'normal')
              doc.setTextColor(55, 65, 81)

              const bulletLines = doc.splitTextToSize(bullet, contentWidth - 8)
              doc.text('-', margin, yPos)
              for (let i = 0; i < bulletLines.length; i++) {
                doc.text(bulletLines[i], margin + 5, yPos)
                if (i < bulletLines.length - 1) yPos += 4
              }
              yPos += 4
              continue
            }

            // Liste numerate
            if (/^\d+\.\s/.test(trimmed)) {
              checkPage(6)
              const numText = clean(trimmed.replace(/\*\*/g, ''))
              doc.setFontSize(10)
              doc.setFont('helvetica', 'normal')
              doc.setTextColor(55, 65, 81)

              const numLines = doc.splitTextToSize(numText, contentWidth)
              for (const nl of numLines) {
                doc.text(nl, margin, yPos)
                yPos += 4
              }
              continue
            }

            // Citazioni
            if (trimmed.startsWith('> ')) {
              checkPage(10)
              const quoteText = clean(trimmed.replace('> ', '').replace(/\*\*/g, ''))
              doc.setFillColor(240, 249, 255)
              doc.setFontSize(10)
              const qLines = doc.splitTextToSize(quoteText, contentWidth - 10)
              const qHeight = qLines.length * 5 + 4
              doc.roundedRect(margin, yPos - 2, contentWidth, qHeight, 2, 2, 'F')
              doc.setDrawColor(59, 130, 246)
              doc.setLineWidth(0.5)
              doc.line(margin, yPos - 2, margin, yPos - 2 + qHeight)
              doc.setTextColor(30, 64, 175)
              for (const ql of qLines) {
                doc.text(ql, margin + 4, yPos + 2)
                yPos += 5
              }
              yPos += 2
              continue
            }

            // Code blocks - salta
            if (trimmed.startsWith('```')) continue

            // Paragrafo normale
            const para = clean(trimmed.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, ''))
            if (para) {
              addText(para, 10, false, [55, 65, 81])
            }
          }

          yPos += 8
        }
      }

      // ========== HEADER E FOOTER ==========
      setProgress(98)
      const total = doc.getNumberOfPages()

      for (let i = 2; i <= total; i++) {
        doc.setPage(i)

        // Header
        doc.setFillColor(71, 85, 105)
        doc.rect(0, 0, pageWidth, 12, 'F')
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.text(`${theme.companyName} - ${theme.appName}`, margin, 8)
        doc.text(`Manuale Utente v${MANUAL_VERSION}`, pageWidth - margin, 8, { align: 'right' })

        // Footer
        doc.setFillColor(241, 245, 249)
        doc.rect(0, pageHeight - 10, pageWidth, 10, 'F')
        doc.setTextColor(100, 116, 139)
        doc.setFontSize(8)
        doc.text(theme.copyright, margin, pageHeight - 4)
        doc.text(`Pagina ${i} di ${total}`, pageWidth - margin, pageHeight - 4, { align: 'right' })
      }

      // Salva
      setProgress(100)
      doc.save(`${theme.appName.replace(/[^a-z0-9]/gi, '_')}_Manuale_Utente_v${MANUAL_VERSION}.pdf`)

    } catch (error) {
      console.error('Errore generazione PDF:', error)
      alert('Errore durante la generazione del PDF. Riprova.')
    } finally {
      setIsGenerating(false)
      setProgress(0)
    }
  }

  return (
    <div className={className}>
      <button
        onClick={generatePdf}
        disabled={isGenerating}
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        {isGenerating ? (
          <>
            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
            <span>Generazione PDF... {progress}%</span>
          </>
        ) : (
          <>
            <ArrowDownTrayIcon className="h-5 w-5" />
            <span>Scarica PDF</span>
          </>
        )}
      </button>

      {isGenerating && (
        <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

    </div>
  )
}
